import { Box, Button, Center, Flex, Group, Modal, SegmentedControl, Stack, Tooltip } from "@mantine/core";
import { IEmbeddedLink } from "@mctypes/global.types";
import { useEffect, useState } from "react";
import { CreateNewButton, McInputNew } from "../../../../components";
import WidgetWrapper from "../../../../components/WidgetWrapper/WidgetWrapper";
import { setShowSuccessMessageThunk } from "../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { EmbedTypeEnum } from "@mcapps/shared/sharedConstants";
import { debounce } from "../../../utils";
import { useSaveEmbeddedLinksMutation } from "../ProjectDashboardRTK";
import { setProjectDashboardActiveLayoutInRedux } from "../projectDashboardSlice";

const EmbeddedLinkWidget: React.FC<{ embedType?: EmbedTypeEnum }> = ({ embedType }) => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const layout = useAppSelector((state) => state.projectDashboardReducer.activeLayout);
  const dispatch = useAppDispatch();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [embeddedLinks, setEmbeddedLinks] = useState<IEmbeddedLink[]>([]);
  const [activeLink, setActiveLink] = useState<number>(0);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editModeLinks, setEditModeLinks] = useState<IEmbeddedLink[]>([]);
  const [activePreview, setActivePreview] = useState<number>(0);
  const isVideoFeed = embedType === EmbedTypeEnum.VIDEOFEED ? true : false;
  const [saveEmbeddedLinks] = useSaveEmbeddedLinksMutation();
  //filteredByType allows for multiple embed widgets, using the same component
  const filteredByType = embeddedLinks.filter((link) => {
    if (embedType === EmbedTypeEnum.EMBEDDEDLINK) {
      return link?.embedType === EmbedTypeEnum.EMBEDDEDLINK || !link?.embedType;
    } else {
      return link?.embedType === embedType;
    }
  });
  const notFilteredByType = embeddedLinks.filter((link) => !filteredByType.includes(link));
  useEffect(() => {
    if (layout?.widgetData?.embeddedLinks) {
      setEmbeddedLinks(layout.widgetData.embeddedLinks);
    }
  }, [layout]);

  useEffect(() => {
    if (editMode === true) {
      return setEditModeLinks(filteredByType);
    }
    return setEditModeLinks([]);
  }, [editMode]);

  const save = async () => {
    const combinedLinks = [...notFilteredByType, ...editModeLinks];
    if (layout._id != null) {
      await saveEmbeddedLinks({ projectId: project._id, links: combinedLinks, layoutId: layout._id });
    }
    dispatch(setShowSuccessMessageThunk(isVideoFeed === true ? "Byggeplasskamera lagret" : "Appkobling lagret"));

    //updates the layout in redux in case user changes layout w/o refreshing
    const updatedLayout = { ...layout, widgetData: { ...layout.widgetData, embeddedLinks: combinedLinks } };
    dispatch(setProjectDashboardActiveLayoutInRedux(updatedLayout));
    setEditMode(false);
  };

  const handleCloseModal = async () => {
    await save();
    setEditMode(false);
  };

  const edit = debounce((key: string, index: number, value: any) => {
    const _links = [...editModeLinks];
    if (_links[index]) {
      const updatedLink = { ..._links[index], [key]: value };
      _links[index] = updatedLink;
    } else {
      _links.push({ name: "", url: "", embedType: embedType });
    }
    setEditModeLinks(_links);
  }, 200);
  const onChange = (key: string, index: number, value: any) => {
    edit(key, index, value);
  };

  const add = () => {
    const _links = [...editModeLinks];
    _links.push({
      name: "",
      url: "",
      embedType: embedType,
    });
    setEditModeLinks(_links);
  };

  const remove = (index: number) => {
    const _links = [...editModeLinks];
    _links.splice(index, 1);
    setEditModeLinks(_links);
  };

  const DisplayPreview: React.FC<{ src: string; fitWidth?: boolean }> = ({ src, fitWidth = false }) => {
    if (src && src.includes("byggekamera.no")) {
      return <img src={src} style={{ width: fitWidth ? "100%" : undefined, height: fitWidth ? undefined : "100%" }} />;
    } else {
      return (
        <iframe
          style={{ minHeight: `${fitWidth ? "70vh" : "100%"}` }}
          width="100%"
          src={src}
          frameBorder="0"
          scrolling="yes"
          allow="fullscreen; clipboard-read; clipboard-write"
          allowFullScreen
        ></iframe>
      );
    }
  };

  const regexp =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?!&//=]*)/gi;

  const filteredUrl = (url) => {
    const isValidUrl = url.match(regexp)?.toString();
    if (isValidUrl) {
      return isValidUrl.includes("miro.com") ? isValidUrl + "&embedAutoplay=true&autoplay=yep" : isValidUrl;
    } else return "";
  };

  const validLinks = editModeLinks.filter((link) => link.name.length > 0 && filteredUrl(link.url).length > 0);
  const linksMap = editModeLinks.map((link, index) => {
    return (
      <Flex key={index} w={"100%"} align={"center"} wrap="nowrap" gap={"xs"}>
        <McInputNew
          placeholder="Tittel"
          style={{ width: "40%" }}
          value={link.name}
          onChange={(e) => onChange("name", index, e.target.value)}
        />
        <McInputNew
          style={{ outline: `${filteredUrl(link.url).length < 1 ? "1px solid red" : ""}` }}
          placeholder="Lim embed-url:"
          value={link.url}
          onChange={(e) => onChange("url", index, filteredUrl(e.target.value))}
        />
        <Button
          variant="outline"
          color="red"
          size="xs"
          px={"xs"}
          style={{ marginLeft: "0.25rem" }}
          onClick={() => remove(index)}
        >
          <i className="fa fa-trash fa-fw" />
        </Button>
      </Flex>
    );
  });

  const AppButtonsHeader: React.FC<{ color?: string }> = ({ color }) => {
    return (
      <>
        {filteredByType.length > 0 ? (
          <SegmentedControl
            fullWidth
            mr={4}
            value={String(activeLink)}
            onChange={(value) => setActiveLink(Number(value))}
            color={"mc-primary"}
            size="sm"
            data={filteredByType.map((link, index) => {
              const needsTooltip = (link.name || "").length > 5;

              const labelContent = (
                <Center
                  className="text-truncate"
                  style={{
                    minWidth: 40,
                  }}
                >
                  {link.name}
                </Center>
              );

              return {
                value: String(index),
                label: needsTooltip ? (
                  <Tooltip label={link.name} withinPortal offset={10} position="top" withArrow>
                    {labelContent}
                  </Tooltip>
                ) : (
                  labelContent
                ),
              };
            })}
          />
        ) : (
          <Box component="h4" m="0.25rem">
            {isVideoFeed === true ? "Byggeplasskamera" : "Eksterne Apper"}
          </Box>
        )}
      </>
    );
  };
  return (
    <WidgetWrapper
      widgetKey={embedType}
      customTitle={<AppButtonsHeader />}
      onFullscreenClick={filteredByType.length > 0 ? () => setOpenModal(true) : null}
      onShowSettingsClick={() => setEditMode(!editMode)}
    >
      <>
        <Flex h="100%" justify="center" style={{ overflow: "auto" }} w="100%">
          {filteredByType.length > 0 ? (
            <>{editMode === false && <DisplayPreview src={filteredByType[activeLink]?.url} />}</>
          ) : (
            <Stack justify="center" align="center" h="100%" ta="center" onMouseDown={(e) => e.stopPropagation()}>
              <p>Ingen nettsider er koblett! Klikk her for å håndtere visninger:</p>
              <Button color="mc-primary" size="sm" style={{ zIndex: 3 }} onClick={() => setEditMode(true)}>
                Håndtere {isVideoFeed === true ? "visninger" : "Eksterne Apper"}
              </Button>
            </Stack>
          )}
        </Flex>
        {editMode && (
          <>
            <Modal
              opened={editMode}
              onClose={handleCloseModal}
              title={`Håndtere ${isVideoFeed === true ? "visninger" : "Eksterne Apper"}`}
              size="60vw"
            >
              <Stack gap={"xs"} style={{ minHeight: "200px" }}>
                {linksMap}

                <Group w="100%" gap="xs" align="center" justify="space-between">
                  <Group gap="xs" align="center">
                    <div>Legg til {isVideoFeed ? "visning" : "appkobling"}</div>
                    <CreateNewButton size="sm" onClick={() => add()} />
                  </Group>

                  <Button
                    disabled={validLinks.length !== editModeLinks.length}
                    onClick={handleCloseModal}
                    color="green"
                    size="sm"
                  >
                    <i className="fa fa-floppy-o fa-fw" />
                  </Button>
                </Group>
                {editModeLinks.length > 0 ? (
                  <Box h="100%" mt="1rem" w="100%">
                    <SegmentedControl
                      mr={4}
                      fullWidth={false}
                      value={String(activePreview)}
                      onChange={(value) => setActivePreview(Number(value))}
                      color="mc-primary"
                      size="md"
                      mb={"xs"}
                      data={validLinks.map((link, index) => ({
                        value: String(index),
                        label: (
                          <Center
                            className="text-truncate"
                            style={{
                              minWidth: 40,
                            }}
                          >
                            {link.name}
                          </Center>
                        ),
                      }))}
                    />
                    <div style={{ height: "400px", overflow: "auto", width: "100%" }}>
                      <DisplayPreview src={validLinks[activePreview]?.url} fitWidth={true} />
                    </div>
                  </Box>
                ) : (
                  <Stack justify="center" align="center" h="100%" ta="center">
                    {isVideoFeed === true ? (
                      <Box component="p" w="75%" m="1.5rem">
                        For å integrere et byggeplasskamera, klikk på plussknappen og fyll ut appens URL, en tittel og
                        en valgfri beskrivelse. URL for innebygde apper kan vanligvis finnes på nettstedet til
                        appleverandøren.
                      </Box>
                    ) : (
                      <Box component="p" w="75%" m="1.5rem">
                        {
                          "Eksterne apper kan integreres for å ha enkel tilgang til tilleggsinformasjon uten å forlate MCApps, for eksempel "
                        }
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href="https://learn.microsoft.com/en-us/power-bi/developer/embedded/embed-customer-app#workspace-id"
                        >
                          PowerBI-dashboard
                        </a>
                        {", "}
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href="https://help.miro.com/hc/en-us/articles/360016335640-How-to-embed-Miro-boards-"
                        >
                          Miro-tavler
                        </a>
                        {" eller et "}
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          href="https://support.mural.co/en/articles/6976865-embed-view-only-murals"
                        >
                          Mural-workspace
                        </a>
                        {
                          ". For å integrere en ekstern app, klikk på plussknappen og fyll ut appens URL, en tittel og en valgfri beskrivelse. URL for innebygde apper kan vanligvis finnes på nettstedet til appleverandøren."
                        }
                      </Box>
                    )}
                  </Stack>
                )}
              </Stack>
            </Modal>
          </>
        )}
        {openModal === true && (
          <Modal
            opened={openModal}
            onClose={() => setOpenModal(false)}
            size="85vw"
            withCloseButton
            title={isVideoFeed === true ? "Byggeplasskamera" : "Eksterne Apper"}
          >
            <Group justify="space-between" w="100%">
              <SegmentedControl
                fullWidth
                mr={4}
                value={String(activeLink)}
                onChange={(value) => setActiveLink(Number(value))}
                color="mc-primary"
                size="md"
                mb={"xs"}
                data={filteredByType.map((link, index) => ({
                  value: String(index),
                  label: (
                    <Center
                      className="text-truncate"
                      style={{
                        minWidth: 80,
                        width: "max-content",
                      }}
                    >
                      {link.name}
                    </Center>
                  ),
                }))}
              />
              <Tooltip label="Åpne i ny fane" withArrow>
                <Button
                  variant="secondary-filled"
                  size="sm"
                  onClick={() => window.open(filteredByType[activeLink]?.url, "_blank")}
                  style={{ fontSize: ".9em" }}
                  mr="xs"
                >
                  <i className="fa fa-external-link-square fa-fw" />
                </Button>
              </Tooltip>
            </Group>
            <div style={{ minHeight: "300px", maxHeight: "74vh", overflow: "auto", width: "100%" }}>
              <DisplayPreview src={filteredByType[activeLink]?.url} fitWidth={true} />
            </div>
            {/* Content left commented intentionally; preview uses main widget iframe */}
          </Modal>
        )}
      </>
    </WidgetWrapper>
  );
};

export default EmbeddedLinkWidget;
